import { useCallback, useEffect, useRef, useState } from "react"
import { Terminal as XTerm } from "xterm"
import { FitAddon } from "xterm-addon-fit"
import { WebLinksAddon } from "xterm-addon-web-links"
import "xterm/css/xterm.css"
import type { OpenCodeApi } from "../../lib/opencodeApi"
import { tokenStore } from "../../lib/api"
import { Button } from "../ui"

interface TerminalProps {
  api: OpenCodeApi
  /** Cloud's session id — needed for the ticket, which is scoped to it. */
  sessionId: string
}

// The agent's PTY wire protocol (agent/packages/core/src/pty/protocol.ts):
//
//   server -> client   raw UTF-8 terminal output, except for one control frame
//                      that is a 0x00 byte followed by UTF-8 JSON {cursor}
//   client -> server   raw UTF-8 keystrokes
//
// Both directions matter here. Writing the control frame to xterm prints the
// JSON on screen, and sending anything structured upstream types it into the
// shell — which is why resize goes over HTTP instead.
const CONTROL_FRAME_MARKER = 0x00

export function Terminal({ api, sessionId }: TerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null)
  const xtermRef = useRef<XTerm | null>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const [ptyId, setPtyId] = useState<string | null>(null)
  const [connected, setConnected] = useState(false)

  // ---------- xterm ----------

  useEffect(() => {
    if (!terminalRef.current) return

    const xterm = new XTerm({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: "Menlo, Monaco, 'Courier New', monospace",
      theme: {
        background: "#1a1a1a",
        foreground: "#ffffff",
        cursor: "#ffffff",
      },
    })

    const fitAddon = new FitAddon()
    xterm.loadAddon(fitAddon)
    xterm.loadAddon(new WebLinksAddon())
    xterm.open(terminalRef.current)

    xtermRef.current = xterm
    fitAddonRef.current = fitAddon
    fitAddon.fit()

    return () => {
      xterm.dispose()
      xtermRef.current = null
      fitAddonRef.current = null
    }
  }, [])

  // ---------- pty ----------

  useEffect(() => {
    let cancelled = false

    // Reattach before creating. The agent keeps a PTY alive after its socket
    // closes and replays prior output to the next client — that is what the
    // cursor control frame is for — so switching tabs or reloading should return
    // to the same shell, with the same working directory and history.
    //
    // Creating unconditionally also leaked a bash process per mount, and React
    // StrictMode double-mounts every effect, so opening the tab twice left three
    // shells running in the container.
    const attach = async () => {
      try {
        const existing = await api.listPty()
        const live = existing.find((p) => p?.status === "running")
        if (cancelled) return
        setPtyId(live ? live.id : (await api.createPty("Web Terminal")).id)
      } catch (err) {
        xtermRef.current?.writeln(`\r\n\x1b[31mFailed to open a terminal: ${err}\x1b[0m`)
      }
    }

    void attach()

    return () => {
      cancelled = true
    }
  }, [api])

  // ---------- socket ----------

  useEffect(() => {
    if (!ptyId || !xtermRef.current) return
    const xterm = xtermRef.current
    let ws: WebSocket | null = null
    let disposed = false

    const connect = async () => {
      // A ticket, not the JWT: a browser cannot put a header on a WebSocket, and
      // the only other place to carry a credential is the URL — where a
      // long-lived token would end up in logs and history. This one is
      // single-use and expires in seconds.
      let ticket: string
      try {
        const res = await fetch(`/api/workspace-sessions/${sessionId}/pty-ticket`, {
          method: "POST",
          headers: { Authorization: `Bearer ${tokenStore.get() ?? ""}` },
        })
        if (!res.ok) throw new Error(`ticket request failed: ${res.status}`)
        ticket = (await res.json()).ticket
      } catch (err) {
        xterm.writeln(`\r\n\x1b[31mCould not authorise the terminal: ${err}\x1b[0m`)
        return
      }

      if (disposed) return

      // Scheme from the page, so an https deployment gets wss without a second
      // place to configure it.
      const scheme = window.location.protocol === "https:" ? "wss" : "ws"
      const url = `${scheme}://${window.location.host}/api/workspace-sessions/${sessionId}/proxy/api/pty/${ptyId}/connect?ticket=${encodeURIComponent(ticket)}`

      ws = new WebSocket(url)
      ws.binaryType = "arraybuffer"
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        // Resize over HTTP. Sending this down the socket would type it into the
        // shell, because inbound frames are keystrokes.
        const dims = fitAddonRef.current?.proposeDimensions()
        if (dims) void api.resizePty(ptyId, dims.cols, dims.rows).catch(() => {})
      }

      ws.onmessage = (event) => {
        if (typeof event.data === "string") {
          xterm.write(event.data)
          return
        }

        const bytes = new Uint8Array(event.data as ArrayBuffer)
        // The cursor control frame. Swallowed rather than printed — it is
        // bookkeeping for resuming a session, not terminal output.
        if (bytes.length > 0 && bytes[0] === CONTROL_FRAME_MARKER) return
        xterm.write(bytes)
      }

      ws.onclose = () => {
        setConnected(false)
        if (!disposed) xterm.writeln("\r\n\x1b[33mDisconnected from terminal\x1b[0m")
      }

      ws.onerror = () => {
        // onclose always follows, and reporting both prints the same failure
        // twice. The console keeps the detail.
        console.error("[Terminal] WebSocket error")
      }
    }

    void connect()

    const onData = xterm.onData((data) => {
      if (ws?.readyState === WebSocket.OPEN) ws.send(data)
    })

    return () => {
      disposed = true
      onData.dispose()
      ws?.close()
      wsRef.current = null
    }
  }, [ptyId, sessionId, api])

  // ---------- resize ----------

  useEffect(() => {
    const onResize = () => {
      fitAddonRef.current?.fit()
      const dims = fitAddonRef.current?.proposeDimensions()
      if (dims && ptyId) void api.resizePty(ptyId, dims.cols, dims.rows).catch(() => {})
    }
    const element = terminalRef.current
    const observer = element ? new ResizeObserver(onResize) : null
    if (element) observer?.observe(element)
    window.addEventListener("resize", onResize)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", onResize)
    }
  }, [ptyId, api])

  const reconnect = useCallback(async () => {
    wsRef.current?.close()
    if (ptyId) {
      // Removed rather than reused: a reconnect is what someone reaches for when
      // the shell is wedged, so it should get them a new one.
      await api.deletePty(ptyId).catch(() => {})
    }
    setPtyId(null)
    setConnected(false)
    try {
      const pty = await api.createPty("Web Terminal")
      setPtyId(pty.id)
    } catch (err) {
      xtermRef.current?.writeln(`\r\n\x1b[31mFailed to create terminal: ${err}\x1b[0m`)
    }
  }, [api, ptyId])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border bg-surface px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">Terminal</span>
          <span
            className={`h-2 w-2 rounded-full ${connected ? "bg-success" : "bg-faint"}`}
            title={connected ? "Connected" : "Disconnected"}
          />
        </div>
        <Button variant="ghost" onClick={reconnect}>
          Reconnect
        </Button>
      </div>
      <div ref={terminalRef} className="flex-1 bg-[#1a1a1a]" />
    </div>
  )
}
